import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  MessageSquare, Loader2, Send, Users as UsersIcon, Trash2, Pencil,
  ArrowUpRight, ArrowDownLeft, Plus, Check, X, Zap,
} from "lucide-react";
import { formatApiError } from "./AuthContext";
import { SmsStatusPill, formatSmsTs } from "./SmsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-1";
const LEAD_STAGES = ["New", "Contacted", "Discovery Call Booked", "Qualified", "Proposal", "Won", "Lost"];

export default function SmsPage() {
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState(null);
  const [records, setRecords] = useState({ leads: [], clients: [] });
  const [error, setError] = useState("");

  const [single, setSingle] = useState({ related: "", toNumber: "", templateId: "", body: "" });
  const [sending, setSending] = useState(false);
  const [singleMsg, setSingleMsg] = useState({ error: "", ok: "" });

  const [bulk, setBulk] = useState({ audience: "", templateId: "", body: "" });
  const [preview, setPreview] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [tplForm, setTplForm] = useState({ id: null, name: "", body: "" });
  const [tplSaving, setTplSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, t, m] = await Promise.all([
        axios.get(`${API}/admin/sms/status`),
        axios.get(`${API}/admin/sms/templates`),
        axios.get(`${API}/admin/sms/messages`),
      ]);
      setStatus(s.data);
      setTemplates(t.data.templates);
      setMessages(m.data.messages);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const out = { leads: [], clients: [] };
      try {
        const r = await axios.get(`${API}/admin/leads`, { params: { limit: 300 } });
        out.leads = r.data.leads.map((l) => ({
          id: l.id, phone: l.phone,
          label: `${l.businessName || `${l.firstName} ${l.lastName}`} (${l.sourceLabel})`,
        }));
      } catch (_) {}
      try {
        const r = await axios.get(`${API}/admin/clients`);
        out.clients = r.data.clients.map((c) => ({
          id: c.id, phone: c.phone,
          label: c.businessName || `${c.contactFirstName} ${c.contactLastName}`,
        }));
      } catch (_) {}
      setRecords(out);
    })();
  }, []);

  const applyTemplate = (setter, state, id) => {
    const t = templates.find((x) => x.id === id);
    setter({ ...state, templateId: id, ...(t ? { body: t.body } : {}) });
  };

  const sendSingle = async (e) => {
    e.preventDefault();
    if (!single.body.trim()) return;
    setSending(true);
    setSingleMsg({ error: "", ok: "" });
    try {
      const payload = { body: single.body.trim() };
      if (single.related) {
        const [relatedType, relatedId] = single.related.split("::");
        payload.relatedType = relatedType;
        payload.relatedId = relatedId;
      } else if (single.toNumber.trim()) {
        payload.toNumber = single.toNumber.trim();
      } else {
        setSingleMsg({ error: "Pick a contact or enter a number.", ok: "" });
        setSending(false);
        return;
      }
      await axios.post(`${API}/admin/sms/send`, payload);
      setSingle({ related: "", toNumber: "", templateId: "", body: "" });
      setSingleMsg({ error: "", ok: "Message sent." });
      await load();
    } catch (err) {
      setSingleMsg({ error: formatApiError(err), ok: "" });
    } finally {
      setSending(false);
    }
  };

  const previewAudience = async (audience) => {
    setBulk({ ...bulk, audience });
    setPreview(null);
    setBulkResult(null);
    if (!audience) return;
    try {
      const r = await axios.get(`${API}/admin/sms/audience-preview`, { params: { audience } });
      setPreview(r.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const sendBulk = async (e) => {
    e.preventDefault();
    if (!bulk.audience || !bulk.body.trim()) return;
    setSendingBulk(true);
    setBulkResult(null);
    setError("");
    try {
      const r = await axios.post(`${API}/admin/sms/send-bulk`, {
        audience: bulk.audience, body: bulk.body.trim(),
      });
      setBulkResult(r.data);
      setBulk({ audience: "", templateId: "", body: "" });
      setPreview(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSendingBulk(false);
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!tplForm.name.trim() || !tplForm.body.trim()) return;
    setTplSaving(true);
    setError("");
    try {
      if (tplForm.id) {
        await axios.patch(`${API}/admin/sms/templates/${tplForm.id}`, { name: tplForm.name.trim(), body: tplForm.body });
      } else {
        await axios.post(`${API}/admin/sms/templates`, { name: tplForm.name.trim(), body: tplForm.body });
      }
      setTplForm({ id: null, name: "", body: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setTplSaving(false);
    }
  };

  const deleteTemplate = async (t) => {
    try {
      await axios.delete(`${API}/admin/sms/templates/${t.id}`);
      if (tplForm.id === t.id) setTplForm({ id: null, name: "", body: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-sms" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">SMS</h1>
        <p className="mt-2 text-mid-neutral">
          Text leads and clients directly from the CRM. Replies land here too.
        </p>
      </header>

      {error && (
        <div data-testid="sms-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Status */}
      <section data-testid="sms-status" className="rounded-2xl bg-forest text-white p-5 flex flex-wrap items-center gap-x-8 gap-y-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-lime">Sending from</p>
          <p className="text-lg font-extrabold" data-testid="sms-from-number">{status?.fromNumber || "—"}</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-lime">Twilio balance</p>
          <p className="text-lg font-extrabold" data-testid="sms-balance">
            {status?.balance != null ? `${status.balance} ${status.currency}` : "—"}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-lime">Sent / Received</p>
          <p className="text-lg font-extrabold" data-testid="sms-counts">
            {status ? `${status.sentTotal} / ${status.receivedTotal}` : "—"}
          </p>
        </div>
        <div className="ml-auto text-xs text-white/60">Account {status?.accountTail || ""}</div>
      </section>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Send single */}
        <section className="rounded-2xl bg-white border border-border-neutral p-6">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
            <Send className="h-4 w-4 text-mint" /> Send a message
          </h2>
          <form onSubmit={sendSingle} className="mt-4 space-y-3">
            <div>
              <label className={labelCls}>Contact</label>
              <select value={single.related} onChange={(e) => setSingle({ ...single, related: e.target.value, toNumber: "" })}
                data-testid="sms-single-contact" className={inputCls}>
                <option value="">Select lead or client…</option>
                <optgroup label="Leads">
                  {records.leads.map((l) => <option key={`l-${l.id}`} value={`lead::${l.id}`}>{l.label}</option>)}
                </optgroup>
                <optgroup label="Clients">
                  {records.clients.map((c) => <option key={`c-${c.id}`} value={`client::${c.id}`}>{c.label}</option>)}
                </optgroup>
              </select>
            </div>
            {!single.related && (
              <div>
                <label className={labelCls}>Or enter a mobile number</label>
                <input value={single.toNumber} onChange={(e) => setSingle({ ...single, toNumber: e.target.value })}
                  placeholder="04xx xxx xxx" data-testid="sms-single-number" className={inputCls} />
              </div>
            )}
            <div>
              <label className={labelCls}>Template</label>
              <select value={single.templateId} onChange={(e) => applyTemplate(setSingle, single, e.target.value)}
                data-testid="sms-single-template" className={inputCls}>
                <option value="">Insert a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <textarea rows={3} value={single.body} onChange={(e) => setSingle({ ...single, body: e.target.value })}
                placeholder="Type your message…" data-testid="sms-single-body" className={inputCls} />
              <p className="mt-1 text-right text-[11px] text-mid-neutral">{single.body.length} / 1600</p>
            </div>
            {singleMsg.error && <p data-testid="sms-single-error" className="text-sm text-red-700">{singleMsg.error}</p>}
            {singleMsg.ok && <p data-testid="sms-single-ok" className="text-sm font-semibold text-forest">{singleMsg.ok}</p>}
            <div className="flex justify-end">
              <button type="submit" disabled={!single.body.trim() || sending}
                data-testid="sms-single-send"
                className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send SMS
              </button>
            </div>
          </form>
        </section>

        {/* Bulk */}
        <section className="rounded-2xl bg-white border border-border-neutral p-6">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
            <UsersIcon className="h-4 w-4 text-mint" /> Bulk send
          </h2>
          <form onSubmit={sendBulk} className="mt-4 space-y-3">
            <div>
              <label className={labelCls}>Audience</label>
              <select value={bulk.audience} onChange={(e) => previewAudience(e.target.value)}
                data-testid="sms-bulk-audience" className={inputCls}>
                <option value="">Select audience…</option>
                <option value="all-leads">All leads</option>
                <option value="all-clients">All clients</option>
                <optgroup label="Leads by stage">
                  {LEAD_STAGES.map((s) => <option key={s} value={`stage:${s}`}>{s} leads</option>)}
                </optgroup>
              </select>
              {preview && (
                <p data-testid="sms-bulk-preview" className="mt-2 text-xs font-semibold text-forest">
                  {preview.recipients} recipient{preview.recipients === 1 ? "" : "s"} with a valid mobile
                  {preview.sample.length > 0 && ` · e.g. ${preview.sample.join(", ")}`}
                </p>
              )}
            </div>
            <div>
              <label className={labelCls}>Template</label>
              <select value={bulk.templateId} onChange={(e) => applyTemplate(setBulk, bulk, e.target.value)}
                data-testid="sms-bulk-template" className={inputCls}>
                <option value="">Insert a template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <textarea rows={3} value={bulk.body} onChange={(e) => setBulk({ ...bulk, body: e.target.value })}
                placeholder="Message to everyone in this audience…" data-testid="sms-bulk-body" className={inputCls} />
              <p className="mt-1 flex justify-between text-[11px] text-mid-neutral">
                <span>{"{firstName}"} and {"{businessName}"} are filled in per recipient.</span>
                <span>{bulk.body.length} / 1600</span>
              </p>
            </div>
            {bulkResult && (
              <p data-testid="sms-bulk-result" className="text-sm font-semibold text-forest">
                Sent {bulkResult.sent} of {bulkResult.recipients}
                {bulkResult.failed.length > 0 && ` — ${bulkResult.failed.length} failed`}
              </p>
            )}
            <div className="flex justify-end">
              <button type="submit" disabled={!bulk.audience || !bulk.body.trim() || sendingBulk}
                data-testid="sms-bulk-send"
                className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                {sendingBulk ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                Send to audience
              </button>
            </div>
          </form>
        </section>
      </div>

      {/* Templates */}
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
          <MessageSquare className="h-4 w-4 text-mint" /> Templates
        </h2>
        <p className="mt-1 text-xs text-mid-neutral">
          Use {"{firstName}"} and {"{businessName}"} as placeholders — they're filled in per recipient.
        </p>
        <div className="mt-4 space-y-2">
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-row-${t.id}`} className="flex items-start gap-3 rounded-xl bg-soft/50 p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-forest">{t.name}</p>
                <p className="text-xs text-mid-neutral whitespace-pre-wrap">{t.body}</p>
              </div>
              <button type="button" onClick={() => setTplForm({ id: t.id, name: t.name, body: t.body })}
                data-testid={`template-edit-${t.id}`}
                className="inline-flex items-center gap-1 rounded-lg bg-white border border-border-neutral text-forest px-2.5 py-1.5 text-xs font-bold">
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button type="button" onClick={() => deleteTemplate(t)}
                data-testid={`template-delete-${t.id}`}
                className="inline-flex items-center gap-1 rounded-lg bg-red-50 text-red-700 px-2.5 py-1.5 text-xs font-bold">
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          ))}
        </div>
        <form onSubmit={saveTemplate} className="mt-4 rounded-xl border border-dashed border-border-neutral p-4 space-y-3">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral">
            {tplForm.id ? "Edit template" : "New template"}
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })}
              placeholder="Template name" data-testid="template-name" className={inputCls + " bg-white"} />
            <div className="sm:col-span-2">
              <input value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })}
                placeholder="Message body…" data-testid="template-body" className={inputCls + " bg-white"} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            {tplForm.id && (
              <button type="button" onClick={() => setTplForm({ id: null, name: "", body: "" })}
                data-testid="template-cancel"
                className="inline-flex items-center gap-1 rounded-lg bg-soft text-forest px-3 py-2 text-xs font-bold">
                <X className="h-3 w-3" /> Cancel
              </button>
            )}
            <button type="submit" disabled={!tplForm.name.trim() || !tplForm.body.trim() || tplSaving}
              data-testid="template-save"
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
              {tplSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : tplForm.id ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              {tplForm.id ? "Save changes" : "Add template"}
            </button>
          </div>
        </form>
      </section>

      {/* History */}
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
          <MessageSquare className="h-4 w-4 text-mint" /> Message history
        </h2>
        <div data-testid="sms-history" className="mt-4 space-y-2">
          {messages === null && (
            <p className="flex items-center text-sm text-mid-neutral"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</p>
          )}
          {messages && messages.length === 0 && <p className="text-sm text-mid-neutral">No messages yet.</p>}
          {(messages || []).map((m) => (
            <div key={m.id} data-testid={`sms-msg-${m.id}`} className="flex items-start gap-3 rounded-xl bg-soft/50 p-3">
              <div className={"mt-0.5 h-7 w-7 rounded-full inline-flex items-center justify-center shrink-0 " +
                (m.direction === "outbound" ? "bg-forest text-lime" : "bg-blue-100 text-blue-800")}>
                {m.direction === "outbound" ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownLeft className="h-3.5 w-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-forest whitespace-pre-wrap">{m.body}</p>
                <p className="mt-1 text-xs text-mid-neutral">
                  {m.relatedName ? (
                    <Link
                      to={m.relatedType === "lead" ? `/admin/leads/${m.relatedId}` : `/admin/clients/${m.relatedId}`}
                      data-testid={`sms-related-${m.id}`}
                      className="font-semibold text-forest hover:underline"
                    >
                      {m.relatedName}
                    </Link>
                  ) : (m.direction === "outbound" ? m.toNumber : m.fromNumber)}
                  {" · "}{m.direction === "outbound" ? `to ${m.toNumber}` : `from ${m.fromNumber}`}
                  {" · "}{formatSmsTs(m.createdAt)}
                  {m.sentByName && ` · by ${m.sentByName}`}
                </p>
              </div>
              <SmsStatusPill status={m.status} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
